import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import  EventCard  from '../components/Eventcard';

const CATEGORIES = ['All', 'Birthday', 'Wedding', 'Corporate', 'Social', 'Conference', 'Other'];

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('all');
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    if (!user) return;
    fetchEvents();
    // eslint-disable-next-line
  }, [activeTab, user]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      let q;
      const eventsRef = collection(db, 'events');

      if (activeTab === 'my-events') {
        // Events created by the current user
        q = query(eventsRef, where('creatorId', '==', user.uid), orderBy('date', 'asc'));
      } else if (activeTab === 'invited') {
        // Events where user is in the attendees subcollection
        // We query a top-level attendees collection or invited array
        q = query(eventsRef, where('invitedUsers', 'array-contains', user.uid), orderBy('date', 'asc'));
      } else {
        // All public future events
        q = query(
          eventsRef,
          where('isPublic', '==', true),
          orderBy('date', 'asc')
        );
      }

      const snap = await getDocs(q);
      const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setEvents(data);
      setFiltered(data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering by search + category
  useEffect(() => {
    let result = events;

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (e) =>
          e.title?.toLowerCase().includes(lower) ||
          e.location?.city?.toLowerCase().includes(lower) ||
          e.location?.address?.toLowerCase().includes(lower)
      );
    }

    if (selectedCategory !== 'All') {
      result = result.filter((e) => e.category === selectedCategory);
    }

    setFiltered(result);
  }, [searchTerm, selectedCategory, events]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    setSelectedCategory('All');
  };

  const tabStyle = (tab) => ({
    padding: '1rem 1.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontWeight: activeTab === tab ? 'bold' : 'normal',
    color: activeTab === tab ? '#6366f1' : '#6b7280',
    borderBottom: activeTab === tab ? '3px solid #6366f1' : '3px solid transparent',
    transition: 'all 0.2s',
    fontSize: '0.95rem',
  });

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: '2rem',
      }}>
        <h1 style={{ color: '#1f2937', margin: 0 }}>
          Welcome, {user?.displayName || user?.email}!
        </h1>
        <Link
          to="/create-event"
          style={{
            backgroundColor: '#6366f1', color: 'white',
            padding: '0.75rem 1.5rem', borderRadius: '8px',
            textDecoration: 'none', fontWeight: 'bold',
          }}
        >
          + Create Event
        </Link>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '0.5rem',
        borderBottom: '2px solid #e5e7eb', marginBottom: '1.5rem',
      }}>
        <button onClick={() => handleTabChange('all')} style={tabStyle('all')}>All Events</button>
        <button onClick={() => handleTabChange('my-events')} style={tabStyle('my-events')}>My Events</button>
        <button onClick={() => handleTabChange('invited')} style={tabStyle('invited')}>Invited Events</button>
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <span style={{
            position: 'absolute', left: '0.75rem', top: '50%',
            transform: 'translateY(-50%)', pointerEvents: 'none',
          }}>🔍</span>
          <input
            type="text"
            placeholder="Search by title or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.25rem',
              border: '1.5px solid #e5e7eb', borderRadius: '8px',
              fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#6366f1')}
            onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
          />
        </div>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb',
            borderRadius: '8px', fontSize: '0.95rem',
            backgroundColor: 'white', color: '#374151',
            cursor: 'pointer', outline: 'none', minWidth: '160px',
          }}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat === 'All' ? 'All Categories' : cat}
            </option>
          ))}
        </select>

        {(searchTerm || selectedCategory !== 'All') && (
          <button
            onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}
            style={{
              padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb',
              borderRadius: '8px', backgroundColor: 'white',
              color: '#6b7280', cursor: 'pointer', fontSize: '0.9rem',
            }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Results count */}
      {!loading && (searchTerm || selectedCategory !== 'All') && (
        <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {filtered.length === 0
            ? 'No events match your search'
            : `${filtered.length} event${filtered.length !== 1 ? 's' : ''} found`}
        </p>
      )}

      {/* Events grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h3 style={{ color: '#6b7280' }}>Loading events...</h3>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '3rem',
          backgroundColor: '#f9fafb', borderRadius: '8px',
        }}>
          <h3 style={{ color: '#6b7280' }}>No events found</h3>
          <p style={{ color: '#9ca3af' }}>
            {searchTerm || selectedCategory !== 'All'
              ? 'Try adjusting your search or filters'
              : activeTab === 'my-events'
              ? 'Create your first event!'
              : 'No events available at the moment'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1.5rem',
        }}>
          {filtered.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
